import Dialog from "@/molecules/dialog";
import { styles } from "./style";
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  MenuItem,
} from "@/common/styles/style";
import DataInput from "@/atoms/dataInput";
import { useCallback, useEffect, useState } from "react";
import RegisterTemplatedDialog from "@/organisms/registerTemplateDialog";
import { formatDateTime, lastWeek, today } from "@/helpers/getUtcOffset";

interface ReportDownloadDialogProps {
  open: boolean;
  onClose: () => void;
  machineId: number;
}

interface Template {
  id: number;
  templateName: string;
  createAt?: string;
}

interface ApiResponse<T = null> {
  message: string;
  errorCode: number | null;
  result: T | null;
}

const apiRequest = async <T = null,>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> => {
  const response = await fetch(path, {
    ...options,
    credentials: "include",
  });

  let data: ApiResponse<T>;

  try {
    data = await response.json();
  } catch {
    throw new Error("サーバーから不正なレスポンスが返されました。");
  }

  if (!response.ok) {
    throw new Error(data.message || "APIエラーが発生しました。");
  }

  return data;
};

export default function ReportDownloadDialog({
  open,
  onClose,
  machineId,
}: ReportDownloadDialogProps) {
  const [startDate, setStartDate] = useState<string>(formatDateTime(lastWeek));

  const [endDate, setEndDate] = useState<string>(formatDateTime(today));

  const [measurementInterval, setMeasurementInterval] = useState<string>("1h");

  // テンプレートIDを保持
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);

  // テンプレート一覧
  const [templates, setTemplates] = useState<Template[]>([]);

  const [isRegisterTemplateOpen, setIsRegisterTemplateOpen] = useState(false);

  // 測定時間間隔の選択肢
  const measurementIntervals = [
    { label: "5分", value: "5m" },
    { label: "10分", value: "10m" },
    { label: "15分", value: "15m" },
    { label: "30分", value: "30m" },
    { label: "1時間", value: "1h" },
    { label: "2時間", value: "2h" },
    { label: "3時間", value: "3h" },
  ];

  // テンプレート一覧取得
  const fetchTemplates = useCallback(async () => {
    try {
      const data = await apiRequest<Template[]>(
        `/api/machines/${machineId}/report-templates`,
        {
          method: "GET",
        },
      );

      const fetchedTemplates = Array.isArray(data.result) ? data.result : [];

      setTemplates(fetchedTemplates);

      setSelectedTemplate((currentSelectedTemplate) => {
        const exists = fetchedTemplates.some(
          (template) => template.id === currentSelectedTemplate,
        );

        return exists
          ? currentSelectedTemplate
          : (fetchedTemplates[0]?.id ?? null);
      });
    } catch (error) {
      console.error("テンプレート一覧取得エラー:", error);

      setTemplates([]);
      setSelectedTemplate(null);
    }
  }, [machineId]);

  // ダイアログ表示時にテンプレート一覧を取得
  useEffect(() => {
    if (open) {
      void fetchTemplates();
    } else {
      setSelectedTemplate(null);
      setMeasurementInterval("1h");
      setStartDate(formatDateTime(lastWeek));
      setEndDate(formatDateTime(today));
    }
  }, [open, fetchTemplates]);

  // FromとToの前後関係をチェック
  const isDateRangeInvalid =
    !!startDate && !!endDate && new Date(startDate) >= new Date(endDate);

  const isDownloadDisabled =
    !startDate ||
    !endDate ||
    isDateRangeInvalid ||
    selectedTemplate === null ||
    !measurementInterval ||
    templates.length === 0;

  const handleDateChange = useCallback(
    (type: "start" | "end") => (value: string) => {
      const dateObj = new Date(value);

      if (type === "start") {
        setStartDate(formatDateTime(dateObj));
      } else if (type === "end") {
        setEndDate(formatDateTime(dateObj));
      }
    },
    [],
  );

  // 帳票ダウンロード
  const handleDownload = async () => {
    if (
      !startDate ||
      !endDate ||
      selectedTemplate === null ||
      !measurementInterval
    ) {
      return;
    }

    try {
      const params = new URLSearchParams({
        templateId: String(selectedTemplate),
        from: startDate,
        to: endDate,
        measurementInterval,
      });

      const data = await apiRequest<{
        fileName: string;
        downloadUrl: string;
      }>(`/api/machines/${machineId}/report?${params.toString()}`, {
        method: "GET",
      });

      if (!data.result) {
        throw new Error("帳票ファイル情報を取得できませんでした。");
      }

      const { fileName, downloadUrl } = data.result;

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("帳票ダウンロードエラー:", error);

      alert(
        error instanceof Error
          ? error.message
          : "帳票のダウンロードに失敗しました。",
      );
    }
  };

  const dataInputField = [
    {
      label: "From",
      value: startDate,
      onchangeKey: "start" as const,
    },
    {
      label: "To",
      value: endDate,
      onchangeKey: "end" as const,
    },
  ];

  // テンプレート登録ダイアログを開く
  const handleRegisterTemplate = () => {
    setIsRegisterTemplateOpen(true);
  };

  // テンプレート登録・更新完了
  const handleRegisterComplete = async () => {
    setIsRegisterTemplateOpen(false);

    // 登録・更新後にテンプレート一覧を再取得する
    await fetchTemplates();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title="帳票ダウンロード"
        sx={styles.container}
        contentChildren={
          <Box>
            <Typography variant="h6" sx={styles.title}>
              期間
            </Typography>

            <Stack direction="row" spacing={2} sx={styles.dateArea}>
              {dataInputField.map((dataField) => (
                <DataInput
                  datatype="TextField"
                  label={dataField.label}
                  textProps={{
                    type: "datetime-local",
                    value: dataField.value,
                    sx: styles.dataInput,
                  }}
                  onChange={handleDateChange(dataField.onchangeKey)}
                  key={dataField.label}
                />
              ))}
            </Stack>

            {isDateRangeInvalid && (
              <Typography sx={styles.errorText}>
                FromにはToより前の日時を指定してください。
              </Typography>
            )}

            <Typography variant="h6" sx={styles.title}>
              測定時間間隔
            </Typography>

            <TextField
              select
              value={measurementInterval}
              onChange={(e) => setMeasurementInterval(e.target.value)}
              sx={styles.templateSelectWrapper}
              size="small"
            >
              {measurementIntervals.map((interval) => (
                <MenuItem key={interval.value} value={interval.value}>
                  {interval.label}
                </MenuItem>
              ))}
            </TextField>

            <Typography variant="h6" sx={styles.title}>
              テンプレート
            </Typography>

            <Stack
              direction="row"
              alignItems="center"
              spacing={5}
              sx={styles.templateArea}
            >
              <TextField
                select
                value={selectedTemplate ?? ""}
                onChange={(e) => setSelectedTemplate(Number(e.target.value))}
                sx={styles.templateSelectWrapper}
                size="small"
              >
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.templateName}
                  </MenuItem>
                ))}
              </TextField>

              <Button
                variant="contained"
                onClick={handleRegisterTemplate}
                sx={styles.registerButton}
              >
                <Typography variant="button">登録</Typography>
              </Button>
            </Stack>

            {templates.length > 0 && selectedTemplate !== null && (
              <Typography sx={styles.templateDate}>
                {
                  templates.find((template) => template.id === selectedTemplate)
                    ?.createAt
                }{" "}
                作成
              </Typography>
            )}

            {templates.length === 0 && (
              <Box sx={styles.templateError}>
                <Typography sx={styles.errorText}>
                  ※テンプレートが登録されていません。
                </Typography>

                <Typography sx={styles.errorText}>
                  「登録」ボタンからテンプレートを作成してください。
                </Typography>
              </Box>
            )}
          </Box>
        }
        actionsChildren={
          <Stack direction="row" spacing={2} sx={styles.buttonArea}>
            <Button variant="outlined" onClick={onClose}>
              <Typography variant="button">キャンセル</Typography>
            </Button>

            <Button
              variant="contained"
              onClick={handleDownload}
              disabled={isDownloadDisabled}
            >
              <Typography variant="button">ダウンロード</Typography>
            </Button>
          </Stack>
        }
      />

      <RegisterTemplatedDialog
        open={isRegisterTemplateOpen}
        onClose={() => setIsRegisterTemplateOpen(false)}
        onRegisterComplete={handleRegisterComplete}
        machineId={machineId}
      />
    </>
  );
}
